/**
 * Medication API — Swiss medication (SAI) drug lookup.
 *
 * Backs the compatibility app's off-matrix drug search: each searched drug
 * name is looked up directly in SAI and rendered as a structured info card
 * (active ingredient, ATC class, Fachinformation sections) — NOT routed
 * through the chat agent.
 */

import { api } from '../client'

/** One labelled section of a drug's Swiss Fachinformation. */
export interface MedicationSection {
  section: string
  content: string
}

/** One drug's SAI record (aggregated from per-section chunks server-side). */
export interface MedicationInfo {
  authorization_number?: string
  trade_name: string
  medication_name?: string
  atc_code?: string
  active_ingredients: string[]
  url?: string
  sections: MedicationSection[]
}

/** Which knowledge tier answered the lookup, and its source name. */
export interface MedicationSource {
  /** Empty when no tier had the drug; otherwise the answering tier. */
  tier: 'internal' | 'national' | 'international' | ''
  /** Stable English source identifier, e.g. "Swiss Medic (SAI)", "NHS dm+d (UK)". */
  name: string
}

/**
 * The answer to a term LOOKUP: the records the answering tier held, the term
 * they were resolved for, and which tier answered.
 *
 * NOT the Page envelope, deliberately: `limit` bounds chunks/products at the
 * tier rather than records, and the SAI service clamps it silently, so no
 * continuation signal can be READ here — and a last page (no `rel="next"`)
 * would assert the absence of rows the caller was never shown. `results` is always an array,
 * never null.
 */
export interface MedicationSearchResponse {
  query: string
  source: MedicationSource
  results: MedicationInfo[]
}

/**
 * Look a drug up by name in Swiss medication data (SAI). Resolves to the
 * matching record(s); `results` is empty when the term isn't found.
 */
export function searchMedication(term: string): Promise<MedicationSearchResponse> {
  return api.get<MedicationSearchResponse>(`/medications?term=${encodeURIComponent(term)}`)
}
